<template>
  <div class="space-y-2">
    <div v-if="!showRaw" class="space-y-2">
      <div
        v-for="(row, idx) in rows"
        :key="idx"
        class="flex flex-wrap items-center gap-2"
      >
        <!-- AND / OR connector lives before every row except the first. -->
        <select
          v-if="idx > 0"
          v-model="row.connector"
          @change="emitValue"
          class="w-20 border border-gray-300 dark:bg-gray-600 rounded px-2 py-1 text-sm"
        >
          <option value="and">AND</option>
          <option value="or">OR</option>
        </select>

        <!-- Direction qualifier: only on primitives that allow it. -->
        <select
          v-if="primitiveFor(row).qualifier"
          v-model="row.qualifier"
          @change="emitValue"
          class="w-40 border border-gray-300 dark:bg-gray-600 rounded px-2 py-1 text-sm"
        >
          <option :value="null">(any direction)</option>
          <option value="src">source</option>
          <option value="dst">destination</option>
        </select>

        <!-- Primitive picker. Long labels; keyword emitted to dumpcap. -->
        <select
          v-model="row.primitive"
          @change="onPrimitiveChange(row)"
          class="w-60 border border-gray-300 dark:bg-gray-600 rounded px-2 py-1 text-sm"
        >
          <option v-for="p in primitives" :key="p.key" :value="p.key">
            {{ p.label }}
          </option>
        </select>

        <!-- Value input, only for primitives that take one. -->
        <input
          v-if="primitiveFor(row).value"
          v-model="row.value"
          @input="emitValue"
          type="text"
          :placeholder="primitiveFor(row).placeholder || ''"
          class="w-52 border border-gray-300 dark:bg-gray-600 rounded px-2 py-1 text-sm font-mono"
        />

        <!-- Negation toggle: corresponds to a leading 'not'. -->
        <label class="flex items-center space-x-1 text-xs text-gray-600 dark:text-gray-300">
          <input type="checkbox" v-model="row.negate" @change="emitValue" />
          <span>NOT</span>
        </label>

        <button
          v-if="rows.length > 1"
          type="button"
          @click="removeRow(idx)"
          class="text-xs text-red-600 hover:text-red-800"
        >
          Remove
        </button>
      </div>

      <button
        type="button"
        @click="addRow"
        class="text-sm text-blue-600 hover:text-blue-800"
      >
        + Add term
      </button>
    </div>

    <div v-else>
      <textarea
        v-model="rawFilter"
        @input="emitValue"
        rows="3"
        placeholder="e.g. host 1.2.3.4 and (port 443 or port 80)"
        class="w-full border border-gray-300 dark:bg-gray-600 rounded px-2 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
      ></textarea>
      <p class="text-xs text-gray-500 mt-1">
        Raw mode accepts any expression dumpcap accepts. The server validates
        syntax on save with <code>tcpdump -d</code>.
      </p>
    </div>

    <div class="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
      <div class="text-xs text-gray-500 dark:text-gray-400 break-all">
        <span class="font-medium">Filter:</span>
        <code class="ml-1">{{ compiledPreview || '(none)' }}</code>
      </div>
      <button
        type="button"
        @click="toggleRaw"
        class="text-xs text-blue-600 hover:text-blue-800 underline flex-shrink-0 ml-2"
      >
        {{ showRaw ? 'Back to builder' : 'Edit as raw filter' }}
      </button>
    </div>
  </div>
</template>

<script>
// Primitives the builder exposes. Long labels for new users; the keyword is
// what dumpcap/libpcap actually parses. `qualifier` means src/dst prefixes are
// accepted; `value` means the primitive takes an argument.
const PRIMITIVES = [
  { key: "host",      label: "Host (IP or name)",         value: true,  qualifier: true,  placeholder: "1.2.3.4 or hostname" },
  { key: "net",       label: "Network (CIDR or mask)",    value: true,  qualifier: true,  placeholder: "10.0.0.0/24" },
  { key: "port",      label: "Port",                       value: true,  qualifier: true,  placeholder: "443 or https" },
  { key: "portrange", label: "Port range",                 value: true,  qualifier: true,  placeholder: "1000-2000" },
  { key: "proto",     label: "Protocol (name or number)",  value: true,  qualifier: false, placeholder: "tcp / udp / 50" },
  { key: "tcp",       label: "TCP",                        value: false, qualifier: false },
  { key: "udp",       label: "UDP",                        value: false, qualifier: false },
  { key: "icmp",      label: "ICMP",                       value: false, qualifier: false },
  { key: "icmp6",     label: "ICMPv6",                     value: false, qualifier: false },
  { key: "arp",       label: "ARP",                        value: false, qualifier: false },
  { key: "ip",        label: "IPv4",                       value: false, qualifier: false },
  { key: "ip6",       label: "IPv6",                       value: false, qualifier: false },
  { key: "vlan",      label: "VLAN (802.1Q)",              value: true,  qualifier: false, placeholder: "optional vlan id" },
  { key: "broadcast", label: "Broadcast",                  value: false, qualifier: false },
  { key: "multicast", label: "Multicast",                  value: false, qualifier: false },
  { key: "less",      label: "Packet size \u2264 (bytes)", value: true,  qualifier: false, placeholder: "1500" },
  { key: "greater",   label: "Packet size \u2265 (bytes)", value: true,  qualifier: false, placeholder: "60" },
];

function emptyRow() {
  return { connector: "and", qualifier: null, primitive: "host", value: "", negate: false };
}

function buildRow(row, primitivesByKey) {
  const p = primitivesByKey[row.primitive];
  if (!p) return "";
  const parts = [];
  if (row.negate) parts.push("not");
  if (p.qualifier && row.qualifier) parts.push(row.qualifier);
  parts.push(row.primitive);
  if (p.value && row.value) parts.push(row.value.trim());
  return parts.join(" ").trim();
}

function buildAll(rows, primitivesByKey) {
  const terms = [];
  rows.forEach((row, i) => {
    const term = buildRow(row, primitivesByKey);
    if (!term) return;
    if (i === 0 || terms.length === 0) {
      terms.push(term);
    } else {
      terms.push(`${row.connector} ${term}`);
    }
  });
  return terms.join(" ").trim();
}

// Best-effort parse of a raw filter back into rows. Catches simple
// space-separated expressions (e.g. "src host 1.2.3.4 and port 80"); anything
// with parentheses, arithmetic, or byte-offsets falls back to raw mode
// because the builder can't express them anyway.
function tryParse(raw) {
  if (!raw) return [emptyRow()];
  if (/[()\[\]<>=!&|]/.test(raw)) return null;
  const tokens = raw.trim().split(/\s+/);
  const rows = [];
  let current = emptyRow();
  current.connector = "and";
  let phase = "start";
  const primitiveKeys = new Set(PRIMITIVES.map((p) => p.key));
  const qualifiers = new Set(["src", "dst"]);

  for (let i = 0; i < tokens.length; i++) {
    const tok = tokens[i].toLowerCase();
    if (phase === "start") {
      if (tok === "not") { current.negate = true; continue; }
      if (qualifiers.has(tok)) { current.qualifier = tok; continue; }
      if (primitiveKeys.has(tok)) {
        current.primitive = tok;
        const p = PRIMITIVES.find((pp) => pp.key === tok);
        phase = p.value ? "value" : "done";
        continue;
      }
      return null;
    } else if (phase === "value") {
      current.value = tokens[i];
      phase = "done";
    } else if (phase === "done") {
      if (tok === "and" || tok === "or") {
        rows.push(current);
        current = emptyRow();
        current.connector = tok;
        phase = "start";
      } else {
        return null;
      }
    }
  }
  if (phase === "done") rows.push(current);
  else if (phase !== "start" || rows.length === 0) return null;
  return rows.length ? rows : [emptyRow()];
}

export default {
  name: "BpfFilterBuilder",
  props: {
    modelValue: { type: String, default: "" },
  },
  emits: ["update:modelValue"],
  data() {
    const parsed = tryParse(this.modelValue);
    return {
      rows: parsed || [emptyRow()],
      showRaw: parsed === null && !!this.modelValue,
      rawFilter: this.modelValue || "",
    };
  },
  computed: {
    primitives() { return PRIMITIVES; },
    primitivesByKey() {
      const byKey = {};
      PRIMITIVES.forEach((p) => { byKey[p.key] = p; });
      return byKey;
    },
    compiledPreview() {
      return this.showRaw ? this.rawFilter : buildAll(this.rows, this.primitivesByKey);
    },
  },
  watch: {
    modelValue(newVal) {
      // Accept upstream resets (e.g. form cleared) without clobbering local
      // edits that match the current output.
      if (newVal === this.compiledPreview) return;
      const parsed = tryParse(newVal);
      if (parsed) {
        this.rows = parsed;
        this.rawFilter = newVal || "";
        this.showRaw = false;
      } else {
        this.rawFilter = newVal || "";
        this.showRaw = true;
      }
    },
  },
  methods: {
    primitiveFor(row) {
      return this.primitivesByKey[row.primitive] || PRIMITIVES[0];
    },
    onPrimitiveChange(row) {
      const p = this.primitiveFor(row);
      if (!p.value) row.value = "";
      if (!p.qualifier) row.qualifier = null;
      this.emitValue();
    },
    addRow() {
      this.rows.push(emptyRow());
      this.emitValue();
    },
    removeRow(idx) {
      this.rows.splice(idx, 1);
      if (this.rows.length === 0) this.rows.push(emptyRow());
      this.emitValue();
    },
    toggleRaw() {
      if (!this.showRaw) {
        // Switching TO raw: seed with the current compiled expression.
        this.rawFilter = buildAll(this.rows, this.primitivesByKey);
        this.showRaw = true;
      } else {
        // Switching BACK to builder: try to parse what the user typed. If we
        // can't, keep raw mode and warn next render.
        const parsed = tryParse(this.rawFilter);
        if (parsed) {
          this.rows = parsed;
          this.showRaw = false;
        } else {
          // Leave in raw mode; user's filter is too complex for the builder.
          this.showRaw = true;
        }
      }
      this.emitValue();
    },
    emitValue() {
      this.$emit("update:modelValue", this.compiledPreview);
    },
  },
};
</script>
